// covered-by: frontends/carrie/src/routes/ask-puntt/hooks/__tests__/useAskPunttDesktopNotifications.test.ts
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import { useUiPrefs } from "./useUiPrefs";

import { canAccessAskPuntt } from "../../../utils/auth";
import { useAskPunttAllChats } from "../../../utils/queries/askPuntt";
import {
  ASK_PUNTT_UNREAD_HIDDEN_POLL_MS,
  buildUnreadChatSnapshots,
  findNewlyUnreadChats,
  getDesktopNotificationPermission,
  isDesktopNotificationSupported,
  showAskPunttChatDesktopNotification,
  type UnreadChatSnapshot,
  resolveActiveAskPunttChatId,
} from "../utils/askPunttDesktopNotifications";

/**
 * Polls chat lists while the tab is hidden and raises native Notification
 * toasts when a background chat becomes unread. Opt-in via ui prefs.
 */
export function useAskPunttDesktopNotifications(): void {
  const navigate = useNavigate();
  const { prefs } = useUiPrefs();
  const enabled = prefs.askPunttDesktopNotifications === true;

  const [, setVisibilityTick] = useState(0);

  const location = useRouterState({
    select: (state) => ({
      pathname: state.location.pathname,
      search: state.location.search as Record<string, unknown>,
    }),
  });
  const activeChatId = resolveActiveAskPunttChatId(location);

  const previousSnapshotsRef = useRef<Map<string, UnreadChatSnapshot>>(
    new Map(),
  );
  const lastNotifiedAtByChatRef = useRef<Map<string, string>>(new Map());
  const hasSeededRef = useRef(false);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const onVisibilityChange = () => {
      setVisibilityTick((tick) => tick + 1);
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, []);

  const documentHidden =
    typeof document !== "undefined" && document.visibilityState === "hidden";
  const permission = getDesktopNotificationPermission();
  const shouldPoll =
    enabled &&
    permission === "granted" &&
    canAccessAskPuntt() &&
    isDesktopNotificationSupported() &&
    documentHidden;

  useEffect(() => {
    if (!shouldPoll) {
      hasSeededRef.current = false;
    }
  }, [shouldPoll]);

  const { data } = useAskPunttAllChats({
    scope: "all",
    enabled: shouldPoll,
    refetchInterval: shouldPoll ? ASK_PUNTT_UNREAD_HIDDEN_POLL_MS : false,
  });

  useEffect(() => {
    if (!shouldPoll || data?.chats == null) return;

    const nextSnapshots = buildUnreadChatSnapshots(data.chats);

    if (!hasSeededRef.current) {
      for (const snapshot of Array.from(nextSnapshots.values())) {
        if (snapshot.hasUnread && snapshot.lastMessageAt != null) {
          lastNotifiedAtByChatRef.current.set(
            snapshot.chatId,
            snapshot.lastMessageAt,
          );
        }
      }
      previousSnapshotsRef.current = nextSnapshots;
      hasSeededRef.current = true;
      return;
    }

    const newlyUnread = findNewlyUnreadChats(
      previousSnapshotsRef.current,
      nextSnapshots,
      {
        activeChatId,
        lastNotifiedAtByChat: lastNotifiedAtByChatRef.current,
      },
    );

    for (const snapshot of newlyUnread) {
      showAskPunttChatDesktopNotification(snapshot, (target) => {
        if (target.projectId) {
          void navigate({
            to: "/ask-puntt/projects/$projectId",
            params: { projectId: target.projectId },
            search: { tab: "chats", chatId: target.chatId },
          });
          return;
        }
        void navigate({
          to: "/ask-puntt/$chatId",
          params: { chatId: target.chatId },
        });
      });
      if (snapshot.lastMessageAt != null) {
        lastNotifiedAtByChatRef.current.set(
          snapshot.chatId,
          snapshot.lastMessageAt,
        );
      }
    }

    previousSnapshotsRef.current = nextSnapshots;
  }, [activeChatId, data, navigate, shouldPoll]);
}
